/**
 * Interaction triggers + action types supported by the Forge engine.
 *
 * Source of truth: the engine's runInteractions.ts switch.
 * Adding a new entry here is purely a UI addition — the engine handles
 * unknown action types defensively (logs a warning, no crash) so a new
 * action type can ship in the builder UI before the engine implements it.
 *
 * The vocabulary is mobile-tailored: triggers map to RN gesture / lifecycle
 * events, actions to native APIs (Linking, Alert, Share, Vibration, …) and
 * to react-navigation. Web-only entries from the legacy Burrow days were
 * dropped in favour of mobile-meaningful equivalents.
 */

export const TRIGGER_TYPES = [
  'Primary Tap',
  'Long Press',
  'On Submit',
  'On Focus',
  'On Blur',
  'On View Loaded',
  'On View Unloaded',
  'Data Updated',
] as const;

export type TriggerType = (typeof TRIGGER_TYPES)[number];

export interface ActionTypeMeta {
  label: string;
  /** Hint shown under the value field for this action type */
  valueHint: string;
  /** Whether the engine actually executes this action today, or just logs */
  status: 'wired' | 'logs-only';
  /** UI variant for the value field; falls back to text input. */
  valueInput?: 'screen-name' | 'text';
}

export const ACTION_TYPES: Record<string, ActionTypeMeta> = {
  /* ----------------------------- Navigation ----------------------------- */
  'Navigate to Screen': {
    label: 'Navigate to Screen',
    valueHint:
      'Name of a Screen view in the same app. The view tree must be inside a StackNavigator or TabNavigator.',
    status: 'wired',
    valueInput: 'screen-name',
  },
  'Go Back': {
    label: 'Go Back',
    valueHint: 'Pops the navigation stack. Leave the value field empty.',
    status: 'wired',
  },
  'Replace to Screen': {
    label: 'Replace to Screen',
    valueHint:
      "Like Navigate to Screen but drops the current route from history. Use for one-shot screens (splash, onboarding, etc.) so the user can't back into them.",
    status: 'wired',
    valueInput: 'screen-name',
  },

  /* ------------------------------ Drawer ------------------------------- */
  'Open Drawer': {
    label: 'Open Drawer',
    valueHint:
      "Name (View Tree label) of the Drawer to open. Drawer state is shared by name across the whole app.",
    status: 'wired',
  },
  'Close Drawer': {
    label: 'Close Drawer',
    valueHint: 'Name of the Drawer to close. Tap on the drawer backdrop also closes it.',
    status: 'wired',
  },
  'Toggle Drawer': {
    label: 'Toggle Drawer',
    valueHint: 'Flips the open/closed state of the named Drawer.',
    status: 'wired',
  },

  /* ------------------------------ Timing ------------------------------- */
  'Delay': {
    label: 'Delay',
    valueHint:
      'Pauses the action chain. Value is duration in ms (e.g. 2000 for 2 seconds). Useful for splash sequences: Delay 2000 → Replace to Screen.',
    status: 'wired',
  },

  /* ---------------------------- OS / device ----------------------------- */
  'Open URL': {
    label: 'Open URL',
    valueHint:
      'A URL handed to the OS (https://, mailto:, tel:, etc.). Supports ${...} expressions.',
    status: 'wired',
  },
  'Show Alert': {
    label: 'Show Alert',
    valueHint:
      'Native alert dialog. Use "Title|Message" for both, or just plain text for a single-message alert.',
    status: 'wired',
  },
  'Vibrate': {
    label: 'Vibrate',
    valueHint: 'Duration in ms (default 50). Empty for a short tap-feel buzz.',
    status: 'wired',
  },
  'Copy to Clipboard': {
    label: 'Copy to Clipboard',
    valueHint: 'Text to write to the device clipboard. Supports ${...} expressions.',
    status: 'wired',
  },
  'Share': {
    label: 'Share',
    valueHint:
      "Opens the OS share sheet with this text. Native: Share.share(); web: Web Share API where supported.",
    status: 'wired',
  },
  'Dismiss Keyboard': {
    label: 'Dismiss Keyboard',
    valueHint: 'Hides the on-screen keyboard. Leave the value field empty.',
    status: 'wired',
  },

  /* ------------------ State mutations (still log-only) ------------------ */
  'Set Source': {
    label: 'Set Source',
    valueHint: "Mutates a target view's source binding. Stub-logged today.",
    status: 'logs-only',
  },
  'Set View Property': {
    label: 'Set View Property',
    valueHint: "Mutates a target view's style/property. Stub-logged today.",
    status: 'logs-only',
  },
  'Reload View': {
    label: 'Reload View',
    valueHint: 'Forces a re-fetch on a data-bound view. Stub-logged today.',
    status: 'logs-only',
  },
};

export const ACTION_TYPE_KEYS = Object.keys(ACTION_TYPES);
